/**
 * Modal for transferring channel to devel environment
 */
/* global setTimeout, clearTimeout, alert */

import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {defineMessages, injectIntl} from 'react-intl';
import TransferChannelToDevelMutation from '../../data/mutations/TransferChannelToDevelMutation.js';
import SearchDevelOrganisationsMutation from '../../data/mutations/SearchDevelOrganisationsMutation.js';
import {displayError} from '../app/errorDisplayer';

/* eslint-disable @typescript-eslint/no-var-requires */
const Modal = require('react-bootstrap/lib/Modal');
const Button = require('react-bootstrap/lib/Button');
const FormGroup = require('react-bootstrap/lib/FormGroup');
const FormControl = require('react-bootstrap/lib/FormControl');
const ControlLabel = require('react-bootstrap/lib/ControlLabel');
const HelpBlock = require('react-bootstrap/lib/HelpBlock');
/* eslint-enable @typescript-eslint/no-var-requires */

const messages = defineMessages({
    modalTitle: {
        id: 'transferChannelToDevel.modalTitle',
        defaultMessage: 'Transfer Channel to Devel',
    },
    channelLabel: {
        id: 'transferChannelToDevel.channelLabel',
        defaultMessage: 'Channel:',
    },
    searchLabel: {
        id: 'transferChannelToDevel.searchLabel',
        defaultMessage: 'Search for organization on devel',
    },
    searchPlaceholder: {
        id: 'transferChannelToDevel.searchPlaceholder',
        defaultMessage: 'Enter organization name...',
    },
    searchHelpText: {
        id: 'transferChannelToDevel.searchHelpText',
        defaultMessage: 'Enter name to search for organization',
    },
    loading: {
        id: 'transferChannelToDevel.loading',
        defaultMessage: 'Loading...',
    },
    noResults: {
        id: 'transferChannelToDevel.noResults',
        defaultMessage: 'No organizations found',
    },
    manualInputLink: {
        id: 'transferChannelToDevel.manualInputLink',
        defaultMessage: 'Or enter organization ID manually',
    },
    manualInputLabel: {
        id: 'transferChannelToDevel.manualInputLabel',
        defaultMessage: 'Target Organization ID',
    },
    manualInputPlaceholder: {
        id: 'transferChannelToDevel.manualInputPlaceholder',
        defaultMessage: 'Enter organization UUID...',
    },
    manualInputHelpText: {
        id: 'transferChannelToDevel.manualInputHelpText',
        defaultMessage: 'Enter organization ID from devel environment',
    },
    backToSearchLink: {
        id: 'transferChannelToDevel.backToSearchLink',
        defaultMessage: '← Back to search',
    },
    selectedOrgLabel: {
        id: 'transferChannelToDevel.selectedOrgLabel',
        defaultMessage: 'Selected organization:',
    },
    cancelButton: {
        id: 'transferChannelToDevel.cancelButton',
        defaultMessage: 'Cancel',
    },
    transferButton: {
        id: 'transferChannelToDevel.transferButton',
        defaultMessage: 'Transfer',
    },
    transferringButton: {
        id: 'transferChannelToDevel.transferringButton',
        defaultMessage: 'Transferring...',
    },
    errorLoadOrganisations: {
        id: 'transferChannelToDevel.errorLoadOrganisations',
        defaultMessage: 'Failed to load organizations from devel',
    },
    errorSelectOrganisation: {
        id: 'transferChannelToDevel.errorSelectOrganisation',
        defaultMessage: 'Please select target organization',
    },
    successMessage: {
        id: 'transferChannelToDevel.successMessage',
        defaultMessage: 'Channel successfully transferred!\n\nNew channel ID: {channelId}',
    },
    errorNoConfirmation: {
        id: 'transferChannelToDevel.errorNoConfirmation',
        defaultMessage: 'Transfer completed, but failed to get confirmation',
    },
    errorTransferFailed: {
        id: 'transferChannelToDevel.errorTransferFailed',
        defaultMessage: 'Transfer failed',
    },
    identNumLabel: {
        id: 'transferChannelToDevel.identNumLabel',
        defaultMessage: 'Tax ID:',
    },
    idLabel: {
        id: 'transferChannelToDevel.idLabel',
        defaultMessage: 'ID:',
    },
});

class TransferChannelToDevelModal extends React.Component {
    static propTypes = {
        channel: PropTypes.object.isRequired,
        intl: PropTypes.object.isRequired,
        onClose: PropTypes.func.isRequired,
        show: PropTypes.bool.isRequired,
    };

    constructor(props) {
        super(props);
        this.state = {
            searchQuery: '',
            searchResults: [],
            selectedOrgId: null,
            selectedOrgName: '',
            isSearching: false,
            isTransferring: false,
            useManualInput: false,
            manualOrgId: '',
        };
    }

    componentDidMount() {
        // Auto-search on mount to show some organisations
        this.performSearch('');
    }

    performSearch = (query) => {
        this.setState({ isSearching: true });

        const mutation = new SearchDevelOrganisationsMutation({
            query: query,
            limit: 20,
        });

        Relay.Store.commitUpdate(mutation, {
            onSuccess: (response) => {
                this.setState({
                    searchResults: response.searchDevelOrganisations.organisations || [],
                    isSearching: false,
                });
            },
            onFailure: () => {
                const {intl} = this.props;
                displayError(intl.formatMessage(messages.errorLoadOrganisations));
                this.setState({ isSearching: false });
            },
        });
    };

    handleSearchChange = (e) => {
        const query = e.target.value;
        this.setState({ searchQuery: query });

        // Debounce search
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
        this.searchTimeout = setTimeout(() => {
            this.performSearch(query);
        }, 300);
    };

    handleOrgSelect = (org) => {
        this.setState({
            selectedOrgId: org.id,
            selectedOrgName: org.name,
            useManualInput: false,
        });
    };

    handleManualInput = (e) => {
        this.setState({
            manualOrgId: e.target.value,
            selectedOrgId: e.target.value,
            useManualInput: true,
        });
    };

    handleTransfer = () => {
        const {selectedOrgId} = this.state;
        const {channel, intl} = this.props;

        if (!selectedOrgId || selectedOrgId.trim() === '') {
            displayError(intl.formatMessage(messages.errorSelectOrganisation));
            return;
        }

        this.setState({ isTransferring: true });

        const mutation = new TransferChannelToDevelMutation({
            channel: channel,
            targetOrganisationId: selectedOrgId,
        });

        Relay.Store.commitUpdate(mutation, {
            onSuccess: (response) => {
                const result = response?.transferChannelToDevel?.result;

                if (result && result.newChannelId) {
                    alert(intl.formatMessage(messages.successMessage, {channelId: result.newChannelId}));
                    this.setState({ isTransferring: false });
                    this.props.onClose();
                } else {
                    displayError(intl.formatMessage(messages.errorNoConfirmation));
                    this.setState({ isTransferring: false });
                }
            },
            onFailure: (transaction) => {
                const error = transaction.getError();
                displayError(error?.source?.errors?.[0]?.message || intl.formatMessage(messages.errorTransferFailed));
                this.setState({ isTransferring: false });
            },
        });
    };

    toggleManualInput = () => {
        this.setState({
            useManualInput: !this.state.useManualInput,
            manualOrgId: '',
            selectedOrgId: null,
        });
    };

    render() {
        const {show, onClose, channel, intl} = this.props;
        const {
            searchQuery,
            searchResults,
            selectedOrgId,
            selectedOrgName,
            isSearching,
            isTransferring,
            useManualInput,
            manualOrgId,
        } = this.state;

        return (
            <Modal backdrop="static" onHide={onClose} show={show}>
                <Modal.Header closeButton>
                    <Modal.Title>{intl.formatMessage(messages.modalTitle)}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{marginBottom: 15}}>
                        <strong>{intl.formatMessage(messages.channelLabel)}</strong> {channel?.name}
                    </div>

                    {!useManualInput ? (
                        <div>
                            <FormGroup>
                                <ControlLabel>{intl.formatMessage(messages.searchLabel)}</ControlLabel>
                                <FormControl
                                    disabled={isTransferring}
                                    onChange={this.handleSearchChange}
                                    placeholder={intl.formatMessage(messages.searchPlaceholder)}
                                    type="text"
                                    value={searchQuery}
                                />
                                <HelpBlock>
                                    {intl.formatMessage(messages.searchHelpText)}
                                </HelpBlock>
                            </FormGroup>

                            {isSearching && (
                                <div style={{padding: 10, textAlign: 'center'}}>
                                    {intl.formatMessage(messages.loading)}
                                </div>
                            )}

                            {!isSearching && searchResults.length > 0 && (
                                <div style={{
                                    maxHeight: 200,
                                    overflowY: 'auto',
                                    border: '1px solid #ddd',
                                    borderRadius: 4,
                                    marginBottom: 10,
                                }}>
                                    {searchResults.map((org) => (
                                        <div
                                            key={org.id}
                                            onClick={() => this.handleOrgSelect(org)}
                                            onMouseEnter={(e) => {
                                                if (selectedOrgId !== org.id) {
                                                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (selectedOrgId !== org.id) {
                                                    e.currentTarget.style.backgroundColor = 'white';
                                                }
                                            }}
                                            style={{
                                                padding: 10,
                                                cursor: 'pointer',
                                                backgroundColor: selectedOrgId === org.id ? '#e3f2fd' : 'white',
                                                borderBottom: '1px solid #eee',
                                            }}
                                        >
                                            <div style={{fontWeight: 'bold'}}>{org.name}</div>
                                            <div style={{fontSize: 12, color: '#666'}}>
                                                {org.identNum && `${intl.formatMessage(messages.identNumLabel)} ${org.identNum} | `}
                                                {intl.formatMessage(messages.idLabel)} {org.id}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {!isSearching && searchResults.length === 0 && searchQuery && (
                                <div style={{padding: 10, textAlign: 'center', color: '#999'}}>
                                    {intl.formatMessage(messages.noResults)}
                                </div>
                            )}

                            <div style={{textAlign: 'center', marginTop: 10}}>
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        this.toggleManualInput();
                                    }}
                                    style={{fontSize: 13}}
                                >
                                    {intl.formatMessage(messages.manualInputLink)}
                                </a>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <FormGroup>
                                <ControlLabel>{intl.formatMessage(messages.manualInputLabel)}</ControlLabel>
                                <FormControl
                                    disabled={isTransferring}
                                    onChange={this.handleManualInput}
                                    placeholder={intl.formatMessage(messages.manualInputPlaceholder)}
                                    type="text"
                                    value={manualOrgId}
                                />
                                <HelpBlock>
                                    {intl.formatMessage(messages.manualInputHelpText)}
                                </HelpBlock>
                            </FormGroup>

                            <div style={{textAlign: 'center', marginTop: 10}}>
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        this.toggleManualInput();
                                    }}
                                    style={{fontSize: 13}}
                                >
                                    {intl.formatMessage(messages.backToSearchLink)}
                                </a>
                            </div>
                        </div>
                    )}

                    {selectedOrgId && (
                        <div style={{
                            marginTop: 15,
                            padding: 10,
                            backgroundColor: '#e8f5e9',
                            borderRadius: 4,
                        }}>
                            <strong>{intl.formatMessage(messages.selectedOrgLabel)}</strong> {selectedOrgName || selectedOrgId}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button disabled={isTransferring} onClick={onClose}>
                        {intl.formatMessage(messages.cancelButton)}
                    </Button>
                    <Button
                        bsStyle="primary"
                        disabled={!selectedOrgId || isTransferring}
                        onClick={this.handleTransfer}
                    >
                        {isTransferring ? intl.formatMessage(messages.transferringButton) : intl.formatMessage(messages.transferButton)}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default injectIntl(Relay.createContainer(TransferChannelToDevelModal, {
    fragments: {
        channel: () => Relay.QL`
            fragment on Channel {
                id
                name
                ${TransferChannelToDevelMutation.getFragment('channel')}
            }
        `,
    },
}));

